import React, { useState } from 'react';
import { Activity, Briefcase, DollarSign, Heart, Users, GraduationCap, Plane, Sparkles, ChevronDown, ChevronUp, Calendar, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const TrillionDollarDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [lifeData, setLifeData] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState({});

  const birthData = {
    full_name: 'Mandeep Singh',
    gender: 'Male',
    date: '1997-05-14',
    time: '12:17:00',
    place_name: 'Dehradun',
    latitude: 30.3255646,
    longitude: 78.0436813,
    timezone: 'Asia/Kolkata'
  };

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const generateLifeIntelligence = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/life-intelligence/generate`, birthData);
      setLifeData(response.data.life_intelligence);
      // Auto-expand all domains to show everything
      const allExpanded = {};
      domains.forEach(d => allExpanded[d.id] = true);
      setExpandedDomains(allExpanded);
      toast.success('🎉 Trillion Dollar Dashboard Generated!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const domains = [
    { id: 'health_longevity', name: 'Health & Longevity', icon: <Activity className="w-6 h-6" />, color: 'from-emerald-500 to-green-600' },
    { id: 'career_profession', name: 'Career & Profession', icon: <Briefcase className="w-6 h-6" />, color: 'from-blue-500 to-cyan-600' },
    { id: 'wealth_finance', name: 'Wealth & Finance', icon: <DollarSign className="w-6 h-6" />, color: 'from-yellow-500 to-orange-600' },
    { id: 'relationships_marriage', name: 'Relationships & Marriage', icon: <Heart className="w-6 h-6" />, color: 'from-pink-500 to-rose-600' },
    { id: 'family_children', name: 'Family & Children', icon: <Users className="w-6 h-6" />, color: 'from-purple-500 to-indigo-600' },
    { id: 'education_skills', name: 'Education & Skills', icon: <GraduationCap className="w-6 h-6" />, color: 'from-indigo-500 to-purple-600' },
    { id: 'travel_foreign', name: 'Travel & Foreign', icon: <Plane className="w-6 h-6" />, color: 'from-cyan-500 to-blue-600' },
    { id: 'spirituality_growth', name: 'Spirituality & Growth', icon: <Sparkles className="w-6 h-6" />, color: 'from-violet-500 to-purple-600' }
  ];

  const renderSection = (title, content, icon = null) => (
    <div className="bg-white/5 p-4 rounded-lg border border-white/10 mb-4">
      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
        {icon}
        {title}
      </h4>
      {content}
    </div>
  );

  const renderTable = (data, columns) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map((col, idx) => (
              <th key={idx} className="text-left text-gray-400 pb-2 pr-4">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx} className="border-b border-white/5">
              {Object.values(row).map((val, i) => (
                <td key={i} className="text-gray-300 py-2 pr-4">{val}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // COMPLETE HEALTH RENDER - EVERYTHING
  const renderHealthComplete = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-emerald-500/10 p-3 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs">Health Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.health_score}%</p>
          </div>
          <div className="bg-emerald-500/10 p-3 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs">Longevity</p>
            <p className="text-white text-lg font-bold">{complete_details?.longevity?.estimated_years || 'N/A'} years</p>
          </div>
          <div className="bg-emerald-500/10 p-3 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs">Category</p>
            <p className="text-white text-sm">{complete_details?.longevity?.category || 'N/A'}</p>
          </div>
          <div className="bg-emerald-500/10 p-3 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs">Major Risk</p>
            <p className="text-white text-sm">{dashboard.major_risk}</p>
          </div>
        </div>

        {/* Vital Organs */}
        {renderSection('Vital Organs', (
          <div className="grid grid-cols-2 gap-2">
            {dashboard.vital_organs?.map((organ, idx) => (
              <div key={idx} className="text-gray-300 text-sm">✓ {organ}</div>
            ))}
          </div>
        ))}

        {/* COMPLETE Disease Report */}
        {complete_details?.disease_report && renderSection('Complete Disease Analysis', (
          <div className="space-y-2">
            {Object.entries(complete_details.disease_report).map(([disease, info]) => (
              <div key={disease} className="bg-white/5 p-3 rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">{disease.replace(/_/g, ' ')}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    info.risk === 'Low' || info.risk === 'Very Low' ? 'bg-green-500/20 text-green-300' :
                    info.risk === 'Moderate' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>{info.risk} Risk</span>
                </div>
                {info.years && info.years.length > 0 && (
                  <p className="text-gray-400 text-xs">Risk Years: {info.years.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-4 h-4 text-emerald-400" />)}

        {/* Organ Vitality Scores - ALL */}
        {complete_details?.organ_vitality && renderSection('Organ Vitality Scores (All Organs)', (
          <div className="space-y-3">
            {Object.entries(complete_details.organ_vitality).map(([organ, score]) => (
              <div key={organ}>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-300 text-sm">{organ}</span>
                  <span className="text-emerald-400 font-semibold">{score}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-green-600" style={{width: `${score}%`}} />
                </div>
              </div>
            ))}
          </div>
        ), <Activity className="w-4 h-4 text-emerald-400" />)}

        {/* Surgery Indicators - ALL */}
        {complete_details?.surgery_indicators && complete_details.surgery_indicators.length > 0 && renderSection('Surgery Indicators', (
          <div className="space-y-3">
            {complete_details.surgery_indicators.map((surgery, idx) => (
              <div key={idx} className="bg-red-500/10 p-3 rounded border border-red-500/20">
                <p className="text-white font-medium">{surgery.type}</p>
                <p className="text-gray-300 text-sm mt-1">Years: {surgery.years.join(', ')}</p>
                <p className="text-gray-300 text-sm">Organs: {surgery.organs.join(', ')}</p>
                <p className="text-gray-300 text-sm">Severity: {surgery.severity}</p>
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-4 h-4 text-red-400" />)}

        {/* Accident Indicators - ALL */}
        {complete_details?.accident_indicators && complete_details.accident_indicators.length > 0 && renderSection('Accident Risk Indicators', (
          <div className="space-y-3">
            {complete_details.accident_indicators.map((accident, idx) => (
              <div key={idx} className="bg-orange-500/10 p-3 rounded border border-orange-500/20">
                <p className="text-white font-medium">{accident.type}</p>
                <p className="text-gray-300 text-sm">Years: {accident.years.join(', ')}</p>
                <p className="text-gray-300 text-sm">Severity: {accident.severity}</p>
                <p className="text-gray-400 text-xs mt-1">Remedies: {accident.remedies}</p>
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-4 h-4 text-orange-400" />)}

        {/* Monthly Forecast - ALL 12 MONTHS */}
        {complete_details?.monthly_forecast && renderSection('Monthly Health Forecast (Next 12 Months)', (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {complete_details.monthly_forecast.map((month, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded border border-white/10">
                <p className="text-white font-medium text-sm">{month.month}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-emerald-400 text-xs">Energy: {month.energy_level}%</p>
                  <p className="text-gray-400 text-xs">Risk: {month.risk_level}</p>
                  <p className="text-gray-400 text-xs">Focus: {month.focus.join(', ')}</p>
                </div>
              </div>
            ))}
          </div>
        ), <Calendar className="w-4 h-4 text-emerald-400" />)}

        {/* Yearly Forecast - ALL YEARS */}
        {complete_details?.yearly_forecast && renderSection('Yearly Health Forecast', (
          <div className="space-y-2">
            {complete_details.yearly_forecast.map((year, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded flex justify-between items-center">
                <div>
                  <span className="text-white font-medium">{year.year}</span>
                  <span className="text-gray-400 text-sm ml-3">{year.health_quality}</span>
                </div>
                <div className="text-gray-400 text-xs">
                  {year.major_events.length > 0 ? year.major_events.join(', ') : 'No major events'}
                </div>
              </div>
            ))}
          </div>
        ), <TrendingUp className="w-4 h-4 text-emerald-400" />)}

        {/* Remedies - COMPLETE */}
        {complete_details?.remedies && renderSection('Health Remedies & Recommendations', (
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(complete_details.remedies).map(([category, advice]) => (
              <div key={category} className="bg-green-500/10 p-3 rounded border border-green-500/20">
                <p className="text-green-400 font-medium text-sm mb-1">{category.replace(/_/g, ' ').toUpperCase()}</p>
                <p className="text-gray-300 text-xs">{advice}</p>
              </div>
            ))}
          </div>
        ), <Sparkles className="w-4 h-4 text-green-400" />)}
      </div>
    );
  };

  // COMPLETE CAREER RENDER - EVERYTHING
  const renderCareerComplete = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs">Career Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.career_score}%</p>
          </div>
          <div className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs">Current Phase</p>
            <p className="text-white text-sm">{dashboard.current_phase}</p>
          </div>
          <div className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs">Best Industry</p>
            <p className="text-white text-sm">{dashboard.best_industry}</p>
          </div>
          <div className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs">Next Promotion</p>
            <p className="text-white text-sm">{dashboard.next_promotion}</p>
          </div>
        </div>

        {/* ALL Job Titles by Category */}
        {complete_details?.all_suitable_jobs && renderSection('ALL Suitable Job Titles (50+ Roles)', (
          <div className="space-y-4">
            {Object.entries(complete_details.all_suitable_jobs).map(([category, jobs]) => (
              <div key={category}>
                <h5 className="text-blue-400 font-semibold mb-2">{category}</h5>
                <div className="grid grid-cols-3 gap-2">
                  {jobs.map((job, idx) => (
                    <div key={idx} className="bg-white/5 p-2 rounded text-gray-300 text-sm">
                      • {job}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ), <Briefcase className="w-4 h-4 text-blue-400" />)}

        {/* Company Types - ALL DETAILS */}
        {complete_details?.company_type_analysis && renderSection('Company Type Analysis (Complete)', (
          <div className="space-y-3">
            {Object.entries(complete_details.company_type_analysis).map(([type, info]) => (
              <div key={type} className="bg-white/5 p-3 rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">{type.replace(/_/g, ' ')}</span>
                  <span className="text-blue-400 font-bold">{info.score}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-600" style={{width: `${info.score}%`}} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <p className="text-gray-400">Luck: <span className="text-gray-300">{info.luck}</span></p>
                  <p className="text-gray-400">Timeline: <span className="text-gray-300">{info.timeline}</span></p>
                </div>
              </div>
            ))}
          </div>
        ), <TrendingUp className="w-4 h-4 text-blue-400" />)}

        {/* Complete Salary Timeline */}
        {complete_details?.salary_timeline && renderSection('Complete Salary Timeline (2025-2035)', (
          <div className="space-y-2">
            {complete_details.salary_timeline.map((item, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded flex justify-between items-center">
                <div>
                  <p className="text-white font-medium">{item.year}</p>
                  <p className="text-gray-400 text-xs">{item.promotion}</p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-bold">{item.salary_lpa} LPA</p>
                  <p className="text-gray-400 text-xs">Growth: +{item.growth}</p>
                </div>
              </div>
            ))}
          </div>
        ), <DollarSign className="w-4 h-4 text-green-400" />)}

        {/* Career Changes - ALL */}
        {complete_details?.career_changes && renderSection('Career Changes & Milestones', (
          <div className="space-y-2">
            {complete_details.career_changes.map((change, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded flex justify-between">
                <span className="text-white font-medium">{change.year}</span>
                <span className="text-blue-400">{change.change}</span>
              </div>
            ))}
          </div>
        ))}

        {/* Skill Requirements by Year - ALL */}
        {complete_details?.skill_requirements && renderSection('Skill Requirements Timeline', (
          <div className="space-y-3">
            {Object.entries(complete_details.skill_requirements).map(([year, skills]) => (
              <div key={year} className="bg-white/5 p-3 rounded">
                <p className="text-white font-medium mb-2">{year}</p>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ), <GraduationCap className="w-4 h-4 text-blue-400" />)}

        {/* Work Environment - COMPLETE */}
        {complete_details?.work_environment && renderSection('Work Environment Analysis', (
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(complete_details.work_environment).map(([key, value]) => (
              <div key={key} className="bg-white/5 p-3 rounded">
                <p className="text-gray-400 text-xs">{key.replace(/_/g, ' ')}</p>
                <p className="text-white text-sm">{value}</p>
              </div>
            ))}
          </div>
        ))}

        {/* Career Risks - ALL */}
        {complete_details?.career_risks && renderSection('Career Risks & Challenges', (
          <div className="space-y-2">
            {Object.entries(complete_details.career_risks).map(([year, risk]) => (
              <div key={year} className="bg-red-500/10 p-3 rounded border border-red-500/20">
                <p className="text-white font-medium">{year}</p>
                <p className="text-gray-300 text-sm">{risk}</p>
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-4 h-4 text-red-400" />)}
      </div>
    );
  };

  // COMPLETE WEALTH RENDER - EVERYTHING
  const renderWealthComplete = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-yellow-500/10 p-3 rounded border border-yellow-500/20">
            <p className="text-yellow-400 text-xs">Wealth Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.wealth_score}%</p>
          </div>
          <div className="bg-yellow-500/10 p-3 rounded border border-yellow-500/20">
            <p className="text-yellow-400 text-xs">Net Worth</p>
            <p className="text-white text-sm">{dashboard.current_net_worth}</p>
          </div>
          <div className="bg-yellow-500/10 p-3 rounded border border-yellow-500/20">
            <p className="text-yellow-400 text-xs">Growth</p>
            <p className="text-white text-sm">{dashboard.wealth_growth}</p>
          </div>
        </div>

        {/* Assets Timeline - ALL YEARS */}
        {complete_details?.assets_timeline && renderSection('Complete Assets Timeline (Every Year)', (
          <div className="space-y-4">
            {Object.entries(complete_details.assets_timeline).map(([year, assets]) => (
              <div key={year} className="bg-white/5 p-4 rounded border border-yellow-500/20">
                <h5 className="text-yellow-400 font-bold mb-3">Year {year}</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(assets).filter(([k]) => k !== 'total_assets').map(([key, value]) => (
                    <div key={key} className="bg-white/5 p-2 rounded">
                      <p className="text-gray-400 text-xs">{key.replace(/_/g, ' ')}</p>
                      <p className="text-white font-medium text-sm">{value}</p>
                    </div>
                  ))}
                  <div className="bg-green-500/20 p-2 rounded border border-green-500/30 col-span-full">
                    <p className="text-green-400 text-xs font-semibold">TOTAL ASSETS</p>
                    <p className="text-white font-bold text-lg">{assets.total_assets}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ), <DollarSign className="w-4 h-4 text-yellow-400" />)}

        {/* ALL Income Streams */}
        {complete_details?.income_streams && renderSection('All Income Streams (Complete)', (
          <div className="space-y-2">
            {complete_details.income_streams.map((stream, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded flex justify-between items-center">
                <div>
                  <p className="text-white font-medium">{stream.source}</p>
                  <p className="text-gray-400 text-xs">Stability: {stream.stability} | Growth: {stream.growth}</p>
                </div>
                <p className="text-green-400 font-bold">{stream.amount}</p>
              </div>
            ))}
          </div>
        ), <TrendingUp className="w-4 h-4 text-green-400" />)}

        {/* Investment Plan - COMPLETE */}
        {complete_details?.investment_plan && renderSection('Complete Investment Plan', (
          <div className="space-y-3">
            {Object.entries(complete_details.investment_plan).map(([investment, details]) => (
              <div key={investment} className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
                <p className="text-white font-medium mb-2">{investment.replace(/_/g, ' ')}</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-gray-400">Amount:</span> <span className="text-gray-300">{details.amount}</span></div>
                  <div><span className="text-gray-400">Years:</span> <span className="text-gray-300">{details.years}</span></div>
                  <div><span className="text-gray-400">Returns:</span> <span className="text-green-400">{details.returns}</span></div>
                </div>
              </div>
            ))}
          </div>
        ), <Briefcase className="w-4 h-4 text-blue-400" />)}

        {/* Liabilities - COMPLETE */}
        {complete_details?.liabilities && renderSection('Complete Liabilities Analysis', (
          <div className="space-y-3">
            <div className="bg-white/5 p-3 rounded">
              <p className="text-gray-400 text-sm">Current Loans: <span className="text-white">{complete_details.liabilities.current_loans}</span></p>
            </div>
            {complete_details.liabilities.future_loans && complete_details.liabilities.future_loans.map((loan, idx) => (
              <div key={idx} className="bg-red-500/10 p-3 rounded border border-red-500/20">
                <p className="text-white font-medium">{loan.type} ({loan.year})</p>
                <p className="text-gray-300 text-sm">Amount: {loan.amount} | EMI: {loan.emi}</p>
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-4 h-4 text-red-400" />)}

        {/* Wealth Growth Chart - ALL YEARS */}
        {complete_details?.wealth_growth_chart && renderSection('Complete Wealth Growth Projection (2025-2045)', (
          <div className="space-y-3">
            {complete_details.wealth_growth_chart.map((item, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">{item.year}</span>
                  <span className="text-yellow-400 font-bold text-lg">{item.net_worth}</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-yellow-500 to-orange-600" style={{width: `${Math.min((idx + 1) * 12, 100)}%`}} />
                </div>
              </div>
            ))}
          </div>
        ), <TrendingUp className="w-4 h-4 text-yellow-400" />)}
      </div>
    );
  };

  // COMPLETE RELATIONSHIPS RENDER - EVERYTHING
  const renderRelationshipsComplete = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-pink-500/10 p-3 rounded border border-pink-500/20">
            <p className="text-pink-400 text-xs">Relationship Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.relationship_score}%</p>
          </div>
          <div className="bg-pink-500/10 p-3 rounded border border-pink-500/20">
            <p className="text-pink-400 text-xs">Marriage Year</p>
            <p className="text-white text-lg font-bold">{dashboard.marriage_year}</p>
          </div>
          <div className="bg-pink-500/10 p-3 rounded border border-pink-500/20">
            <p className="text-pink-400 text-xs">Quality</p>
            <p className="text-white text-sm">{dashboard.marriage_quality}</p>
          </div>
        </div>

        {/* Complete Love Timeline */}
        {complete_details?.love_timeline && renderSection('Complete Love Timeline (Year by Year)', (
          <div className="space-y-2">
            {complete_details.love_timeline.map((item, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">{item.year} - {item.phase}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    item.intensity === 'Very High' ? 'bg-pink-600 text-white' :
                    item.intensity === 'High' ? 'bg-pink-500/30 text-pink-300' :
                    'bg-purple-500/30 text-purple-300'
                  }`}>{item.intensity}</span>
                </div>
                <p className="text-gray-300 text-sm">{item.activity}</p>
              </div>
            ))}
          </div>
        ), <Heart className="w-4 h-4 text-pink-400" />)}

        {/* Complete Partner Profile - EVERY DETAIL */}
        {complete_details?.partner_profile && renderSection('Complete Partner Profile (Every Detail)', (
          <div className="space-y-4">
            <div className="bg-white/5 p-4 rounded">
              <h5 className="text-pink-400 font-semibold mb-3">Physical Characteristics</h5>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(complete_details.partner_profile.physical).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-sm"><span className="text-gray-400">{key}:</span> {value}</p>
                ))}
              </div>
            </div>
            <div className="bg-white/5 p-4 rounded">
              <h5 className="text-pink-400 font-semibold mb-3">Personality Traits</h5>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(complete_details.partner_profile.personality).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-sm"><span className="text-gray-400">{key}:</span> {value}</p>
                ))}
              </div>
            </div>
            <div className="bg-white/5 p-4 rounded">
              <h5 className="text-pink-400 font-semibold mb-3">Background</h5>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(complete_details.partner_profile.background).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-sm"><span className="text-gray-400">{key}:</span> {value}</p>
                ))}
              </div>
            </div>
            <div className="bg-pink-500/20 p-4 rounded border border-pink-500/30">
              <h5 className="text-pink-400 font-semibold mb-3">Compatibility Analysis</h5>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(complete_details.partner_profile.compatibility).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-gray-400 text-xs mb-1">{key}</p>
                    <p className="text-white font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ), <Heart className="w-4 h-4 text-pink-400" />)}

        {/* Complete Marriage Details */}
        {complete_details?.marriage_details && renderSection('Complete Marriage Analysis', (
          <div className="space-y-4">
            {Object.entries(complete_details.marriage_details).map(([section, details]) => (
              <div key={section} className="bg-white/5 p-4 rounded">
                <h5 className="text-pink-400 font-medium mb-3">{section.replace(/_/g, ' ').toUpperCase()}</h5>
                <div className="space-y-2">
                  {typeof details === 'object' && Object.entries(details).map(([key, value]) => (
                    <p key={key} className="text-gray-300 text-sm">
                      <span className="text-gray-400">{key.replace(/_/g, ' ')}:</span> {
                        Array.isArray(value) ? value.join(', ') : value
                      }
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ), <Heart className="w-4 h-4 text-pink-400" />)}

        {/* Relationship Dynamics - Fight & Bonding Cycles */}
        {complete_details?.relationship_dynamics && renderSection('Relationship Dynamics (Fights & Bonding)', (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h5 className="text-red-400 font-medium mb-3">Fight Cycles</h5>
              {complete_details.relationship_dynamics.fight_cycles?.map((fight, idx) => (
                <div key={idx} className="bg-red-500/10 p-3 rounded mb-2 border border-red-500/20">
                  <p className="text-white font-medium">{fight.year}</p>
                  <p className="text-gray-300 text-xs">Frequency: {fight.frequency} | Severity: {fight.severity}</p>
                  <p className="text-gray-400 text-xs">{fight.reason}</p>
                </div>
              ))}
            </div>
            <div>
              <h5 className="text-green-400 font-medium mb-3">Bonding Periods</h5>
              {complete_details.relationship_dynamics.bonding_periods?.map((bond, idx) => (
                <div key={idx} className="bg-green-500/10 p-3 rounded mb-2 border border-green-500/20">
                  <p className="text-white font-medium">{bond.year}</p>
                  <p className="text-gray-300 text-xs">Strength: {bond.strength}</p>
                  <p className="text-gray-400 text-xs">{bond.activity}</p>
                </div>
              ))}
            </div>
          </div>
        ), <Heart className="w-4 h-4 text-pink-400" />)}

        {/* Family Planning - Complete Details */}
        {complete_details?.family_planning && renderSection('Complete Family Planning', (
          <div className="space-y-3">
            <p className="text-white font-medium">Total Children: {complete_details.family_planning.children_count}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-500/10 p-4 rounded border border-blue-500/20">
                <h5 className="text-blue-400 font-medium mb-2">First Child</h5>
                {Object.entries(complete_details.family_planning.first_child).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-xs mb-1">
                    <span className="text-gray-400">{key}:</span> {value}
                  </p>
                ))}
              </div>
              <div className="bg-purple-500/10 p-4 rounded border border-purple-500/20">
                <h5 className="text-purple-400 font-medium mb-2">Second Child</h5>
                {Object.entries(complete_details.family_planning.second_child).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-xs mb-1">
                    <span className="text-gray-400">{key}:</span> {value}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ), <Users className="w-4 h-4 text-pink-400" />)}
      </div>
    );
  };

  const renderDomainContent = (domainId, data) => {
    if (!data) return <div className="text-gray-400">Loading...</div>;
    
    switch(domainId) {
      case 'health_longevity':
        return renderHealthComplete(data);
      case 'career_profession':
        return renderCareerComplete(data);
      case 'wealth_finance':
        return renderWealthComplete(data);
      case 'relationships_marriage':
        return renderRelationshipsComplete(data);
      default:
        return <div className="text-gray-400 p-4">Comprehensive render for {domainId} coming in next update...</div>;
    }
  };

  return (
    <CosmicBackground>
      <div className="min-h-screen relative z-10 px-4 py-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 mb-2">
            💎 Trillion Dollar Dashboard 💎
          </h1>
          <p className="text-gray-400 text-lg">EVERY POSSIBLE DETAIL • 2000+ Data Points • Complete Life Blueprint</p>
        </div>

        {/* Generate Button */}
        {!lifeData && (
          <div className="mb-8 text-center">
            <button
              onClick={generateLifeIntelligence}
              disabled={loading}
              className="px-12 py-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white rounded-2xl font-bold text-xl hover:scale-105 transition-transform disabled:opacity-50 shadow-2xl"
            >
              {loading ? '⚡ Generating Trillion Dollar Data...' : '🚀 Generate Trillion Dollar Dashboard'}
            </button>
          </div>
        )}

        {/* Dashboard Content */}
        {lifeData && (
          <div className="space-y-4">
            {domains.map(domain => {
              const domainData = lifeData[domain.id];
              const isExpanded = expandedDomains[domain.id];

              return (
                <div key={domain.id} className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden shadow-2xl">
                  <button
                    onClick={() => toggleDomain(domain.id)}
                    className="w-full p-5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-4 rounded-xl bg-gradient-to-r ${domain.color} shadow-lg`}>
                        {domain.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-xl">{domain.name}</h3>
                        {domainData?.dashboard && (
                          <p className="text-gray-400 text-sm">
                            {Object.entries(domainData.dashboard).length} data points loaded
                          </p>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-7 h-7 text-white" /> : <ChevronDown className="w-7 h-7 text-white" />}
                  </button>

                  {isExpanded && (
                    <div className="p-6 border-t border-white/10 bg-black/20">
                      {renderDomainContent(domain.id, domainData)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CosmicBackground>
  );
};

export default TrillionDollarDashboard;
